import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AgentOrchestratorService } from '../../../app/agents/services/agent-orchestrator/agent-orchestrator.service';
import { GetAgentThreadsService } from '../../../app/agents/services/agent-threads/get-agent-threads/get-agent-threads.service';
import { ChatbotService } from '../../../services/chatbot-services/chatbot.service';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from '../../../services/logger-services/logger.service';
import { StreamingStateService } from '../../services/streaming-state/streaming-state.service';
import { SessionThread } from '../../models/session.models';
import { AgentThread, GetAgentThreadsRequest, ShareAgentThreadRequest } from '../../../app/agents/models';
import { AgentConfigService } from '../../../app/agents/services/agent-config/agent-config.service';
import { RenameAgentThreadService } from 'src/app/agents/services/agent-threads/rename-agent-thread/rename-agent-thread';
import { DeleteAgentThreadService } from 'src/app/agents/services/agent-threads/delete-agent-thread/delete-agent-thread';
import { ShareAgentThreadService } from 'src/app/agents/services/agent-threads/share-agent-thread/share-agent-thread';
import { PopupMessageEvent } from 'src/app/models/chat-sessions.models';
@Component({
  selector: 'app-session-history',
  templateUrl: './session-history.component.html',
  styleUrls: ['./session-history.component.scss']
})
export class SessionHistoryComponent implements OnInit, OnDestroy, OnChanges {
  @Input() agentId: string = '';
  @Input() currentSessionId: string = '';
  @Input() isResponseStreaming: boolean = false;
  
  @Output() sessionSelected = new EventEmitter<SessionThread>();
  @Output() newChatRequested = new EventEmitter<void>();
  @Output() showDeleteSuccessMessage = new EventEmitter<void>();
  @Output() showEditSuccessMessage = new EventEmitter<void>();
  @Output() showShareSuccessMessage = new EventEmitter<void>();
  @Output() showPopupMessage = new EventEmitter<PopupMessageEvent>();

  sessionThreads: SessionThread[] = [];
  /** Total thread count from first load; does not change when lazy loading more */
  totalThreadsCount: number = 0;
  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  selectedItemForAction: SessionThread | null = null;

  /** Reference to the scrollable list container for scroll events */
  @ViewChild('sessionListRef') sessionListRef!: ElementRef;

  /** When false, hide Share in conversation history dropdown (per-agent config options.share). */
  get showShareInHistory(): boolean {
    const config = this.agentConfigService.getAgentConfig(this.agentId);
    return config?.options?.share !== false;
  }
  
  /** Pagination state */
  private currentPage: number = 1;
  private totalPages: number = 1;
  private hasMorePages: boolean = false;
  private static readonly DEFAULT_PAGE_SIZE: number = 20;
  private pageSize: number = SessionHistoryComponent.DEFAULT_PAGE_SIZE;
  /** Page size locked after the first successful load; all subsequent pages use this value to prevent duplicates */
  private lockedPageSize: number | null = null;
  
  /** Thread item height for dynamic page size calculation (in pixels) */
  private static readonly THREAD_ITEM_HEIGHT: number = 40; // Approximate height of each thread item
  private static readonly BUFFER_MULTIPLIER: number = 1.5; // Load 1.5x viewport capacity for smooth scrolling
  
  private readonly subscriptions: Subscription[] = [];
  private userID: string = '';
  private tenantID: string = '';
  private resizeListener: (() => void) | null = null;

  constructor(
    private readonly agentOrchestrator: AgentOrchestratorService,
    private readonly getAgentThreadsService: GetAgentThreadsService,
    private readonly renameAgentThreadService: RenameAgentThreadService,
    private readonly deleteAgentThreadService: DeleteAgentThreadService,
    private readonly shareAgentThreadService: ShareAgentThreadService,
    private readonly chatbotService: ChatbotService,
    private readonly translate: TranslateService,
    private readonly logger: LoggerService,
    private readonly streamingStateService: StreamingStateService,
    private readonly agentConfigService: AgentConfigService
  ) {}

  ngOnInit(): void {
    try {
      this.userID = environment.LoginUserID || '';
      this.tenantID = environment.TenantId || '';
      
      // Set up window resize listener for dynamic page size
      this.setupResizeListener();
      
      // Subscribe to agent ID changes
      const agentSub = this.agentOrchestrator.currentAgentId$.subscribe(agentId => {
        // Reset loading flags from any previous agent's in-flight request.
        // The requests themselves are NOT cancelled -- they will complete and
        // write to the correct agent's cache (guarded by requestAgentId).
        this.isLoading = false;
        this.isLoadingMore = false;

        // Don't load threads for module or agent_hub (hub has no thread list; agent_hub has no API config → would hit localhost)
        if (agentId && agentId !== 'module' && agentId !== 'agent_hub') {
          const previousAgentId = this.agentId;
          this.agentId = agentId;
          if (previousAgentId && previousAgentId !== agentId) {
            this.currentSessionId = '';
          }

          // Resolve page size from agent config (defaults to 20)
          this.resolvePageSize(agentId);
          // Check if threads are already loaded for this agent
          // This check uses the orchestrator service which persists across component lifecycle
          if (this.agentOrchestrator.hasAgentThreadsLoaded(agentId)) {
            // Restore threads from cache instead of calling API
            const cachedThreads = this.agentOrchestrator.getCachedAgentThreads(agentId);
            if (cachedThreads) {
              // Use a copy of the cached array to avoid mutating the cache directly
              this.sessionThreads = [...cachedThreads];
              const cachedTotal = this.agentOrchestrator.getCachedTotalThreadsCount(agentId);
              this.totalThreadsCount = cachedTotal !== null ? cachedTotal : this.sessionThreads.length;
              // Restore pagination state to enable continued lazy loading
              const cachedPagination = this.agentOrchestrator.getCachedThreadsPagination(agentId);
              if (cachedPagination) {
                this.currentPage = cachedPagination.currentPage;
                this.totalPages = cachedPagination.totalPages;
                this.hasMorePages = cachedPagination.hasMorePages;
                if (cachedPagination.pageSize) {
                  this.lockedPageSize = cachedPagination.pageSize;
                }
                this.logger.logInfo(
                  `[SessionHistory] Restored pagination state: page ${this.currentPage}/${this.totalPages}, hasMorePages=${this.hasMorePages}, lockedPageSize=${this.lockedPageSize}`,
                  'ngOnInit'
                );
              }
              this.updateSessionSelection();
              // If user opened sidebar while in a new session (sidebar was closed when they asked), ensure current session appears
              this.ensureCurrentSessionInList();
              this.logger.logInfo(
                `[SessionHistory] Restored ${this.sessionThreads.length} threads from cache (total: ${this.totalThreadsCount})`,
                'ngOnInit'
              );
            }
          } else if (this.agentOrchestrator.isAgentThreadsLoading(agentId)) {
            // Orchestrator already has a request in flight — wait for it instead of duplicating
            this.sessionThreads = [];
            this.isLoading = true;
            const capturedAgentId = agentId;
            const readySub = this.agentOrchestrator.agentThreadsReady$.pipe(
              filter(loadedId => loadedId === capturedAgentId),
              take(1)
            ).subscribe(() => {
              if (this.agentId !== capturedAgentId) return;
              this.restoreThreadsFromCache(capturedAgentId);
              this.isLoading = false;
            });
            this.subscriptions.push(readySub);
          } else {
            // Clear stale threads from previous agent before loading new ones
            this.sessionThreads = [];
            // First time loading - call API (only for real agents with API config, e.g. via_agent, regulations_agent)
            this.loadSessionThreads();
          }
        } else {
          this.sessionThreads = [];
        }
      });
      this.subscriptions.push(agentSub);

      // Subscribe to session removal requests (for removing sessions with minimal history)
      const removalSub = this.agentOrchestrator.sessionRemovalRequested$.subscribe((event) => {
        if (event && event.threadId) {
          this.removeSessionFromList(event.threadId);
        }
      });
      this.subscriptions.push(removalSub);
    } catch (error) {
      this.logger.logError(`Error in ngOnInit: ${error}`, 'SessionHistoryComponent.ngOnInit');
    }
  }

  /**
   * Handle input changes, particularly for currentSessionId
   * Updates session selection state when currentSessionId changes (including when cleared for new chat)
   */
  ngOnChanges(changes: SimpleChanges): void {
    try {
      if (changes['currentSessionId'] && this.sessionThreads.length > 0) {
        this.updateSessionSelection();
      }
    } catch (error) {
      this.logger.logError(`Error in ngOnChanges: ${error}`, 'SessionHistoryComponent.ngOnChanges');
    }
  }

  /**
   * Update session selection based on current session ID
   * Sets is_selected property for matching thread, clears all others
   */
  private updateSessionSelection(): void {
    try {
      this.sessionThreads.forEach(thread => {
        thread.is_selected = thread.thread_id === this.currentSessionId;
      });
    } catch (error) {
      this.logger.logError(`Error updating session selection: ${error}`, 'SessionHistoryComponent.updateSessionSelection');
    }
  }

  /**
   * Ensure the current session (e.g. the one user is in while streaming) appears in the list.
   * When the sidebar was closed and the user asked a question, addNewThread was never called (component not in DOM).
   * When the user opens the sidebar, we load or restore the list; the current session may be missing. Prepend it.
   */
  private ensureCurrentSessionInList(): void {
    try {
      if (!this.currentSessionId || !this.agentId) return;

      // Validate that the current session actually belongs to this agent.
      // Check the orchestrator cache: if the session exists in a DIFFERENT agent's cache, skip it.
      const currentAgentId = this.agentId;
      const orchestratorCurrentAgent = this.agentOrchestrator.getCurrentAgentId();
      if (orchestratorCurrentAgent && orchestratorCurrentAgent !== currentAgentId) {
        this.logger.logInfo(
          `[SessionHistory] Skipping ensureCurrentSessionInList: orchestrator agent (${orchestratorCurrentAgent}) != component agent (${currentAgentId})`,
          'ensureCurrentSessionInList'
        );
        return;
      }

      const alreadyInList = this.sessionThreads.some(t => t.thread_id === this.currentSessionId);
      if (alreadyInList) return;

      const threadName = this.agentOrchestrator.getCurrentThreadName() || 'New conversation';
      const newThread: SessionThread = {
        thread_id: this.currentSessionId,
        thread_name: threadName,
        agent_model: this.agentId,
        message_count: 1,
        created_at: new Date().toISOString(),
        modified_at: new Date().toISOString(),
        last_question: threadName,
        is_selected: true
      };
      this.sessionThreads.forEach(t => t.is_selected = false);
      this.sessionThreads.unshift(newThread);
      this.updateSessionSelection();
      // Increment totalThreadsCount to reflect new thread
      this.totalThreadsCount++;
      // Update cache with updated count and current pagination state
      this.agentOrchestrator.cacheAgentThreads(
        this.agentId, 
        this.sessionThreads, 
        this.totalThreadsCount,
        {
          currentPage: this.currentPage,
          totalPages: this.totalPages,
          hasMorePages: this.hasMorePages,
          pageSize: this.lockedPageSize || this.pageSize
        }
      );
      this.logger.logInfo(
        `[SessionHistory] Ensured current session in list: ${this.currentSessionId}`,
        'ensureCurrentSessionInList'
      );
    } catch (error) {
      this.logger.logError(`Error in ensureCurrentSessionInList: ${error}`, 'SessionHistoryComponent.ensureCurrentSessionInList');
    }
  }

  /**
   * Resolve the page size from the agent config or calculate dynamically based on window size.
   * Priority: 1) Agent config thread_page_size, 2) Dynamic calculation, 3) Default 20
   */
  private resolvePageSize(agentId: string): void {
    try {
      const config = this.agentConfigService.getAgentConfig(agentId);
      
      // Check if agent config has explicit page size
      if (config?.thread_page_size && config.thread_page_size > 0) {
        this.pageSize = config.thread_page_size;
        this.logger.logInfo(
          `[SessionHistory] Using thread_page_size=${this.pageSize} from agent config for ${agentId}`,
          'resolvePageSize'
        );
      } else {
        // Calculate dynamic page size based on window height, using agent-specific min/max bounds
        this.pageSize = this.calculateDynamicPageSize(config);
        this.logger.logInfo(
          `[SessionHistory] Using dynamic page size=${this.pageSize} based on window height for ${agentId}`,
          'resolvePageSize'
        );
      }
    } catch (error) {
      this.pageSize = SessionHistoryComponent.DEFAULT_PAGE_SIZE;
      this.logger.logError(`Error resolving page size: ${error}`, 'SessionHistoryComponent.resolvePageSize');
    }
  }

  /**
   * Calculate dynamic page size based on available viewport height
   * Formula: (viewport height / item height) * buffer multiplier
   * Ensures we load enough threads to fill the viewport + some extra for smooth scrolling
   * 
   * @param config Agent configuration containing thread_page_size_min and thread_page_size_max
   */
  private calculateDynamicPageSize(config?: any): number {
    try {
      // Get min/max bounds from agent config
      // If not specified, use DEFAULT_PAGE_SIZE for min and a reasonable max (100)
      const minPageSize = config?.thread_page_size_min || SessionHistoryComponent.DEFAULT_PAGE_SIZE;
      const maxPageSize = config?.thread_page_size_max || 100; // Use 100 as default max, not 20
      
      // Get session list container height (if available)
      let availableHeight: number;
      
      if (this.sessionListRef && this.sessionListRef.nativeElement) {
        availableHeight = this.sessionListRef.nativeElement.clientHeight;
      } else {
        // Fallback: estimate remaining sidebar height after agent selector (~280px)
        availableHeight = Math.max(window.innerHeight - 280, 200);
      }
      
      // Calculate how many items can fit in viewport
      const itemsInViewport = Math.floor(availableHeight / SessionHistoryComponent.THREAD_ITEM_HEIGHT);
      
      // Apply buffer multiplier for smooth scrolling experience
      const calculatedSize = Math.floor(itemsInViewport * SessionHistoryComponent.BUFFER_MULTIPLIER);
      
      // Clamp between agent-specific min and max
      const clampedSize = Math.max(minPageSize, Math.min(calculatedSize, maxPageSize));
      
      this.logger.logInfo(
        `[SessionHistory] Dynamic page size calculation: viewport=${availableHeight}px, items=${itemsInViewport}, calculated=${calculatedSize}, bounds=[${minPageSize}-${maxPageSize}], final=${clampedSize}`,
        'calculateDynamicPageSize'
      );
      
      return clampedSize;
    } catch (error) {
      this.logger.logError(`Error calculating dynamic page size: ${error}`, 'calculateDynamicPageSize');
      return SessionHistoryComponent.DEFAULT_PAGE_SIZE;
    }
  }

  ngOnDestroy(): void {
    try {
      this.subscriptions.forEach(sub => sub.unsubscribe());
      // Clean up resize listener
      if (this.resizeListener) {
        window.removeEventListener('resize', this.resizeListener);
        this.resizeListener = null;
      }
    } catch (error) {
      this.logger.logError(`Error in ngOnDestroy: ${error}`, 'SessionHistoryComponent.ngOnDestroy');
    }
  }

  /**
   * Set up window resize listener to recalculate page size dynamically
   * Uses debouncing to avoid excessive recalculations
   */
  private setupResizeListener(): void {
    try {
      let resizeTimeout: any;
      
      this.resizeListener = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          // Don't recalculate if page size is locked (pagination in progress)
          if (this.lockedPageSize !== null) {
            return;
          }
          const config = this.agentConfigService.getAgentConfig(this.agentId);
          if (!config?.thread_page_size || config.thread_page_size <= 0) {
            const newPageSize = this.calculateDynamicPageSize(config);
            if (newPageSize !== this.pageSize) {
              this.logger.logInfo(
                `[SessionHistory] Window resized, updating page size from ${this.pageSize} to ${newPageSize}`,
                'setupResizeListener'
              );
              this.pageSize = newPageSize;
            }
          }
        }, 300);
      };
      
      window.addEventListener('resize', this.resizeListener);
    } catch (error) {
      this.logger.logError(`Error setting up resize listener: ${error}`, 'setupResizeListener');
    }
  }

  async loadSessionThreads(): Promise<void> {
    try {
      if (!this.agentId || this.agentId === 'module' || this.agentId === 'agent_hub') {
        this.sessionThreads = [];
        return;
      }

      if (!this.tenantID || !this.userID) {
        this.logger.logError('Missing tenant_id or user_id', 'SessionHistoryComponent.loadSessionThreads');
        return;
      }

      // Capture the agent ID at request time so callbacks always reference the
      // agent that triggered this call, even if the user switches agents before
      // the response arrives.
      const requestAgentId = this.agentId;

      this.isLoading = true;
      this.agentOrchestrator.markAgentThreadsLoading(requestAgentId);
      // Reset pagination state for fresh load
      this.currentPage = 1;
      this.totalPages = 1;
      this.hasMorePages = false;
      this.totalThreadsCount = 0;
      this.lockedPageSize = null;

      const request: GetAgentThreadsRequest = {
        tenant_id: this.tenantID,
        user_id: this.userID,
        page: 1,
        page_size: this.pageSize
      };

      const threads$ = this.getAgentThreadsService.getAgentThreads(request, requestAgentId);

      const sub = threads$.subscribe({
        next: (response) => {
          if (response.status === 'success' && response.data) {
            const threads = response.data.threads.map((thread: AgentThread) => ({
              thread_id: thread.thread_id,
              thread_name: thread.thread_name || 'Untitled Conversation',
              agent_model: requestAgentId,
              message_count: 0,
              created_at: thread.updated_at || new Date().toISOString(),
              modified_at: thread.updated_at || new Date().toISOString(),
              last_question: thread.thread_name || 'No messages yet',
              is_selected: false
            }));

            // Lock page size so all subsequent pages use the same value
            this.lockedPageSize = this.pageSize;

            // Compute pagination from response
            let totalThreadsCount = threads.length;
            let pagination = { currentPage: 1, totalPages: 1, hasMorePages: false, pageSize: this.lockedPageSize };
            if (response.data.pagination) {
              pagination = {
                currentPage: response.data.pagination.current_page,
                totalPages: response.data.pagination.total_pages,
                hasMorePages: response.data.pagination.current_page < response.data.pagination.total_pages,
                pageSize: this.lockedPageSize
              };
              const totalFromApi = response.data.pagination.total_threads;
              totalThreadsCount = (totalFromApi !== undefined && totalFromApi !== null)
                ? totalFromApi
                : threads.length;
            }

            // Always cache for the correct agent -- even if the user already
            // switched away, the data is ready when they switch back.
            this.agentOrchestrator.clearAgentThreadsLoading(requestAgentId);
            this.agentOrchestrator.markAgentThreadsLoaded(requestAgentId);
            this.agentOrchestrator.cacheAgentThreads(
              requestAgentId, threads, totalThreadsCount, pagination
            );

            // Only update the UI if this agent is still the active one
            if (this.agentId !== requestAgentId) {
              return;
            }

            this.sessionThreads = threads;
            this.totalThreadsCount = totalThreadsCount;
            this.currentPage = pagination.currentPage;
            this.totalPages = pagination.totalPages;
            this.hasMorePages = pagination.hasMorePages;

            this.updateSessionSelection();
            this.ensureCurrentSessionInList();

            this.logger.logInfo(
              `[SessionHistory] Loaded and cached ${this.sessionThreads.length} threads, total: ${this.totalThreadsCount} (page ${this.currentPage}/${this.totalPages})`,
              'loadSessionThreads'
            );
          } else {
            this.agentOrchestrator.clearAgentThreadsLoading(requestAgentId);
            if (this.agentId === requestAgentId) {
              this.sessionThreads = [];
            }
            this.logger.logError(
              response.message || 'Failed to load threads',
              'SessionHistoryComponent.loadSessionThreads'
            );
          }
          if (this.agentId === requestAgentId) {
            this.isLoading = false;
          }
        },
        error: (error) => {
          this.agentOrchestrator.clearAgentThreadsLoading(requestAgentId);
          if (this.agentId === requestAgentId) {
            this.sessionThreads = [];
            this.isLoading = false;
          }
          this.logger.logError(
            `Error loading threads: ${error?.message || 'unknown'}`,
            'SessionHistoryComponent.loadSessionThreads'
          );
        }
      });
      
      this.subscriptions.push(sub);
    } catch (error) {
      this.logger.logError(`Error in loadSessionThreads: ${error}`, 'SessionHistoryComponent.loadSessionThreads');
      this.isLoading = false;
    }
  }

  /**
   * Restore threads from the orchestrator's cache into the component state.
   * Used when the orchestrator already loaded threads (e.g. from switchToAgent)
   * and the session-history component was created after the load started.
   */
  private restoreThreadsFromCache(agentId: string): void {
    const cachedThreads = this.agentOrchestrator.getCachedAgentThreads(agentId);
    if (cachedThreads) {
      this.sessionThreads = [...cachedThreads];
      const cachedTotal = this.agentOrchestrator.getCachedTotalThreadsCount(agentId);
      this.totalThreadsCount = cachedTotal !== null ? cachedTotal : this.sessionThreads.length;
      const cachedPagination = this.agentOrchestrator.getCachedThreadsPagination(agentId);
      if (cachedPagination) {
        this.currentPage = cachedPagination.currentPage;
        this.totalPages = cachedPagination.totalPages;
        this.hasMorePages = cachedPagination.hasMorePages;
        if (cachedPagination.pageSize) {
          this.lockedPageSize = cachedPagination.pageSize;
        }
      }
      this.updateSessionSelection();
      this.ensureCurrentSessionInList();
      this.logger.logInfo(
        `[SessionHistory] Restored ${this.sessionThreads.length} threads from orchestrator cache (total: ${this.totalThreadsCount})`,
        'restoreThreadsFromCache'
      );
    }
  }

  /**
   * Load the next page of threads (called when user scrolls to bottom)
   */
  loadMoreThreads(): void {
    try {
      if (this.isLoadingMore || !this.hasMorePages) {
        return;
      }

      // Capture agent ID at request time to detect stale responses
      const requestAgentId = this.agentId;

      this.isLoadingMore = true;
      const nextPage = this.currentPage + 1;
      const effectivePageSize = this.lockedPageSize || this.pageSize;

      const request: GetAgentThreadsRequest = {
        tenant_id: this.tenantID,
        user_id: this.userID,
        page: nextPage,
        page_size: effectivePageSize
      };

      const moreThreads$ = this.getAgentThreadsService.getAgentThreads(request, requestAgentId);

      const sub = moreThreads$.subscribe({
        next: (response) => {
          if (response.status === 'success' && response.data) {
            const newThreads = response.data.threads.map((thread: AgentThread) => ({
              thread_id: thread.thread_id,
              thread_name: thread.thread_name || 'Untitled Conversation',
              agent_model: requestAgentId,
              message_count: 0,
              created_at: thread.updated_at || new Date().toISOString(),
              modified_at: thread.updated_at || new Date().toISOString(),
              last_question: thread.thread_name || 'No messages yet',
              is_selected: false
            }));

            // Compute pagination from response
            let pagination = { currentPage: nextPage, totalPages: this.totalPages, hasMorePages: false, pageSize: effectivePageSize };
            if (response.data.pagination) {
              pagination = {
                currentPage: response.data.pagination.current_page,
                totalPages: response.data.pagination.total_pages,
                hasMorePages: response.data.pagination.current_page < response.data.pagination.total_pages,
                pageSize: effectivePageSize
              };
            }

            // Append to the correct agent's cache, deduplicating by thread_id
            const existingCached = this.agentOrchestrator.getCachedAgentThreads(requestAgentId) || [];
            const existingIds = new Set(existingCached.map((t: any) => t.thread_id));
            const uniqueNewThreads = newThreads.filter((t: any) => !existingIds.has(t.thread_id));
            if (uniqueNewThreads.length < newThreads.length) {
              this.logger.logInfo(
                `[SessionHistory] Deduplicated ${newThreads.length - uniqueNewThreads.length} duplicate thread(s) from page ${nextPage}`,
                'loadMoreThreads'
              );
            }
            const allThreads = [...existingCached, ...uniqueNewThreads];
            this.agentOrchestrator.cacheAgentThreads(
              requestAgentId, allThreads, undefined, pagination
            );

            // Only update the UI if this agent is still the active one
            if (this.agentId !== requestAgentId) {
              return;
            }

            this.sessionThreads = allThreads;
            this.currentPage = pagination.currentPage;
            this.totalPages = pagination.totalPages;
            this.hasMorePages = pagination.hasMorePages;

            this.updateSessionSelection();

            this.logger.logInfo(
              `[SessionHistory] Loaded page ${this.currentPage}/${this.totalPages}, total threads: ${this.sessionThreads.length}`,
              'loadMoreThreads'
            );
          } else {
            if (this.agentId === requestAgentId) {
              this.hasMorePages = false;
            }
            this.logger.logError(
              response.message || 'Failed to load more threads',
              'SessionHistoryComponent.loadMoreThreads'
            );
          }
          if (this.agentId === requestAgentId) {
            this.isLoadingMore = false;
          }
        },
        error: (error) => {
          if (this.agentId === requestAgentId) {
            this.isLoadingMore = false;
            this.hasMorePages = false;
          }
          this.logger.logError(
            `Error loading more threads: ${error?.message || 'unknown'}`,
            'SessionHistoryComponent.loadMoreThreads'
          );
        }
      });

      this.subscriptions.push(sub);
    } catch (error) {
      this.logger.logError(`Error in loadMoreThreads: ${error}`, 'SessionHistoryComponent.loadMoreThreads');
      this.isLoadingMore = false;
    }
  }

  /**
   * Handle scroll event on the session list container.
   * Triggers lazy loading when the user scrolls near the bottom.
   */
  onSessionListScroll(event: Event): void {
    try {
      const element = event.target as HTMLElement;
      if (!element) return;

      // Trigger load when scrolled within 100px of the bottom
      const scrollBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
      if (scrollBottom < 100 && this.hasMorePages && !this.isLoadingMore) {
        this.loadMoreThreads();
      }
    } catch (error) {
      this.logger.logError(`Error in onSessionListScroll: ${error}`, 'SessionHistoryComponent.onSessionListScroll');
    }
  }

  addNewThread(threadName: string, threadId?: string): void {
    try {
      // SECURITY: Generate secure thread ID if not provided
      const secureThreadId = threadId || this.generateSecureThreadId();
      const newThread: SessionThread = {
        thread_id: secureThreadId,
        thread_name: threadName,
        agent_model: this.agentId,
        message_count: 1,
        created_at: new Date().toISOString(),
        modified_at: new Date().toISOString(),
        last_question: threadName,
        is_selected: true
      };

      this.sessionThreads.forEach(t => t.is_selected = false);
      this.sessionThreads.unshift(newThread);
      this.currentSessionId = newThread.thread_id;
      
      // Increment totalThreadsCount to reflect new thread
      this.totalThreadsCount++;
      
      // Update cache with new thread and updated count
      this.agentOrchestrator.cacheAgentThreads(
        this.agentId, 
        this.sessionThreads, 
        this.totalThreadsCount,
        {
          currentPage: this.currentPage,
          totalPages: this.totalPages,
          hasMorePages: this.hasMorePages,
          pageSize: this.lockedPageSize || this.pageSize
        }
      );
    } catch (error) {
      this.logger.logError(`Error in addNewThread: ${error}`, 'SessionHistoryComponent.addNewThread');
    }
  }

  /**
   * Generate secure thread ID using crypto APIs
   * 
   * SECURITY: Uses cryptographically secure random generation
   * Fallback chain: crypto.randomUUID() → crypto.getRandomValues() → timestamp-based
   * 
   * @returns Secure thread ID with 'session_' prefix
   */
  private generateSecureThreadId(): string {
    // SECURITY: Try crypto.randomUUID() first (most secure)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return 'session_' + crypto.randomUUID();
    }
    
    // SECURITY: Fallback to crypto.getRandomValues()
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const array = new Uint8Array(9);
      crypto.getRandomValues(array);
      const randomStr = Array.from(array, byte => byte.toString(36)).join('');
      return 'session_' + Date.now() + '_' + randomStr;
    }
    
    // SECURITY: Last resort - timestamp + performance counter (better than Math.random())
    const timestamp = Date.now();
    const perfNow = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
    return 'session_' + timestamp + '_' + Math.floor(perfNow * 10000).toString(36);
  }

  /**
   * Reload threads (can be called externally)
   * Forces a fresh reload by clearing the loaded flag in orchestrator
   */
  public reloadThreads(): void {
    this.agentOrchestrator.clearAgentThreadsLoaded(this.agentId); // Reset to force reload
    this.loadSessionThreads();
  }

  /**
   * Get threads count
   */
  public getThreadsCount(): number {
    return this.sessionThreads.length;
  }

  /**
   * Remove session from local list (used when confirming streaming popup on sessions with minimal history)
   * Similar to chatbot's removeCurrentSessionIfSingleQuestion logic
   */
  private removeSessionFromList(threadId: string): void {
    try {
      this.logger.logInfo(`[SessionHistory] Removing session from list: ${threadId}`, 'removeSessionFromList');
      
      // Remove the session from the local sessionThreads array
      const lengthBefore = this.sessionThreads.length;
      this.sessionThreads = this.sessionThreads.filter(thread => thread.thread_id !== threadId);
      
      // Decrement totalThreadsCount if a session was actually removed
      if (this.sessionThreads.length < lengthBefore && this.totalThreadsCount > 0) {
        this.totalThreadsCount--;
      }
      
      // Update cache with updated count
      this.agentOrchestrator.cacheAgentThreads(
        this.agentId, 
        this.sessionThreads, 
        this.totalThreadsCount,
        {
          currentPage: this.currentPage,
          totalPages: this.totalPages,
          hasMorePages: this.hasMorePages,
          pageSize: this.lockedPageSize || this.pageSize
        }
      );
      
      this.logger.logInfo(`[SessionHistory] Session removed. Remaining sessions: ${this.sessionThreads.length}`, 'removeSessionFromList');
    } catch (error) {
      this.logger.logError(`Error removing session from list: ${error}`, 'SessionHistoryComponent.removeSessionFromList');
    }
  }

  onSelectSession(thread: SessionThread): void {
    try {
      // Check if the clicked session is already the currently selected session
      if (thread.is_selected) {
        // Same session already selected - just return without any action
        // Don't interrupt ongoing streaming or show any popups
        return;
      }

      // Check for streaming state (similar to chatbot logic)
      if (this.isResponseStreaming || this.streamingStateService.getCurrentStreamingState()) {
        this.logger.logInfo('[SessionHistory] Streaming active - showing confirmation popup', 'onSelectSession');
        this.agentOrchestrator.checkStreamingAndExecute(() => {
          this.executeSessionSelection(thread);
        }, 'session_switch');
        return;
      }

      // Execute immediately if not streaming
      this.executeSessionSelection(thread);
    } catch (error) {
      this.logger.logError(`Error in onSelectSession: ${error}`, 'SessionHistoryComponent.onSelectSession');
    }
  }

  /**
   * Execute session selection logic (separated for reuse in confirmation)
   */
  private executeSessionSelection(thread: SessionThread): void {
    try {
      this.sessionThreads.forEach(t => t.is_selected = false);
      thread.is_selected = true;
      this.currentSessionId = thread.thread_id;
      this.sessionSelected.emit(thread);
    } catch (error) {
      this.logger.logError(`Error in executeSessionSelection: ${error}`, 'SessionHistoryComponent.executeSessionSelection');
    }
  }

  onNewChat(): void {
    try {
      // Check for streaming state (similar to chatbot logic)
      if (this.isResponseStreaming || this.streamingStateService.getCurrentStreamingState()) {
        this.logger.logInfo('[SessionHistory] Streaming active - showing confirmation popup', 'onNewChat');
        this.agentOrchestrator.checkStreamingAndExecute(() => {
          this.executeNewChat();
        }, 'new_chat');
        return;
      }

      // Execute immediately if not streaming
      this.executeNewChat();
    } catch (error) {
      this.logger.logError(`Error in onNewChat: ${error}`, 'SessionHistoryComponent.onNewChat');
    }
  }

  /**
   * Execute new chat logic (separated for reuse in confirmation)
   */
  private executeNewChat(): void {
    try {
      this.newChatRequested.emit();
    } catch (error) {
      this.logger.logError(`Error in executeNewChat: ${error}`, 'SessionHistoryComponent.executeNewChat');
    }
  }

  positionChatMenu(thread: SessionThread, event: Event): void {
    try {
      event.stopPropagation();

      // Close all other dropdowns (and move their menus back from body if present)
      this.sessionThreads.forEach(t => {
        if (t !== thread && t.dropdownVisible) {
          this.closeDropdown(t);
        }
      });

      // If already open, close it
      if (thread.dropdownVisible) {
        this.closeDropdown(thread);
        return;
      }

      const target = event.target as HTMLElement;
      const menuButton = (target?.closest?.('.chat-history-icons button') || target) as HTMLElement;
      if (!menuButton || typeof menuButton.getBoundingClientRect !== 'function') {
        thread.dropdownVisible = true;
        const currentThread = thread;
        setTimeout(() => document.addEventListener('click', () => this.closeDropdown(currentThread), { once: true }), 0);
        return;
      }

      const menuElement = menuButton.closest('.list-view-chatname-input')?.querySelector('.chat-history-menu') as HTMLElement;
      if (!menuElement) {
        thread.dropdownVisible = true;
        const currentThread = thread;
        setTimeout(() => document.addEventListener('click', () => this.closeDropdown(currentThread), { once: true }), 0);
        return;
      }

      // Keep menu in DOM so it scrolls with the session list; position only above/below via class
      (thread as SessionThread & { __menuEl?: HTMLElement }).__menuEl = menuElement;
      this.positionDropdown(menuButton, menuElement);
      menuElement.classList.add('show');
      thread.dropdownVisible = true;

      const onceClose = () => this.closeDropdown(thread);
      setTimeout(() => document.addEventListener('click', onceClose, { once: true }), 0);
    } catch (error) {
      this.logger.logError(`Error in positionChatMenu: ${error}`, 'SessionHistoryComponent.positionChatMenu');
    }
  }

  /** Scroll container visible bottom for above/below menu positioning (same as chat-sessions). */
  private getScrollContainerBottom(el: HTMLElement | null): number | null {
    if (!el?.closest) return null;
    const scrollParent = el.closest('.scrollable-tab, .scrollable-folder');
    if (!scrollParent || !(scrollParent instanceof HTMLElement)) return null;
    return scrollParent.getBoundingClientRect().bottom;
  }

  private getScrollContainerTop(el: HTMLElement | null): number | null {
    if (!el?.closest) return null;
    const scrollParent = el.closest('.scrollable-tab, .scrollable-folder');
    if (!scrollParent || !(scrollParent instanceof HTMLElement)) return null;
    return scrollParent.getBoundingClientRect().top;
  }

  /**
   * Sets above/below class on the dropdown so it opens above when there is not enough space below.
   * Menu stays in DOM with position: absolute so it scrolls with the session list.
   */
  private positionDropdown(menuButton: HTMLElement, menuElement: HTMLElement): void {
    try {
      const buttonRect = menuButton.getBoundingClientRect();
      const margin = 10;
      
      const scrollBottom = this.getScrollContainerBottom(menuButton);
      const effectiveViewportBottom = scrollBottom != null
        ? Math.min(window.innerHeight - margin, scrollBottom)
        : window.innerHeight - margin;

      const scrollTop = this.getScrollContainerTop(menuButton);
      const effectiveViewportTop = scrollTop != null
        ? Math.max(margin, scrollTop)
        : margin;

      const estimatedHeight = 132;
      const menuHeight = menuElement.offsetHeight > 0 ? menuElement.offsetHeight : estimatedHeight;
      
      const spaceBelow = effectiveViewportBottom - (buttonRect.bottom + 5);
      const spaceAbove = buttonRect.top - effectiveViewportTop - 5;
      
      const inLowerHalf = buttonRect.bottom > window.innerHeight * 0.5;
      const preferAbove = inLowerHalf && spaceAbove >= menuHeight;
      const useAbove = (preferAbove || spaceBelow < menuHeight) && spaceAbove >= menuHeight;

      if (useAbove) {
        menuElement.classList.remove('chat-history-menu-below');
        menuElement.classList.add('chat-history-menu-above');
      } else {
        menuElement.classList.remove('chat-history-menu-above');
        menuElement.classList.add('chat-history-menu-below');
      }
    } catch (error) {
      this.logger.logError(`Error in positionDropdown: ${error}`, 'SessionHistoryComponent.positionDropdown');
    }
  }

  private closeDropdown(thread: SessionThread): void {
    try {
      const menuEl = (thread as SessionThread & { __menuEl?: HTMLElement }).__menuEl;
      if (menuEl) {
        menuEl.classList.remove('show');
        (thread as SessionThread & { __menuEl?: HTMLElement }).__menuEl = undefined;
      }
      thread.dropdownVisible = false;
    } catch (error) {
      this.logger.logError(`Error in closeDropdown: ${error}`, 'SessionHistoryComponent.closeDropdown');
    }
  }
  
  renameSession(thread: SessionThread, event: Event): void {
    try {
      event.stopPropagation();
      thread.dropdownVisible = false;
      // Preserve original name to allow cancelling without persisting edits
      if (!(thread as any).original_name) {
        (thread as any).original_name = thread.thread_name;
      }
      // Truncate thread name to 100 characters if it exceeds the limit
      if (thread.thread_name && thread.thread_name.length > 100) {
        thread.thread_name = thread.thread_name.substring(0, 100);
      }
      thread.is_edited = true;
      this.selectedItemForAction = thread;
    } catch (error) {
      this.logger.logError(`Error in renameSession: ${error}`, 'SessionHistoryComponent.renameSession');
    }
  }

  confirmDelete(thread: SessionThread, event: Event): void {
    try {
      event.stopPropagation();
      thread.dropdownVisible = false;
      thread.isDeleting = true;
      this.selectedItemForAction = thread;
    } catch (error) {
      this.logger.logError(`Error in confirmDelete: ${error}`, 'SessionHistoryComponent.confirmDelete');
    }
  }

  deleteSession(thread: SessionThread): void {
    try {
      // Close the delete confirmation popup immediately
      thread.isDeleting = false;
      this.selectedItemForAction = null;
      
      const request = {
        thread_id: thread.thread_id,
        user_id: this.userID,
        tenant_id: this.tenantID 
      }
      this.deleteAgentThreadService.DeleteAgentThread(request, this.agentId).subscribe({
        next: (response) => {
          if (response.status?.toLowerCase() === 'success') {
            const index = this.sessionThreads.findIndex(t => t.thread_id === thread.thread_id);
            if (index > -1) {
              this.sessionThreads.splice(index, 1);
            }
            // Decrement totalThreadsCount to reflect deletion
            if (this.totalThreadsCount > 0) {
              this.totalThreadsCount--;
            }
            // Update cache after deletion with updated count
            this.agentOrchestrator.cacheAgentThreads(
              this.agentId, 
              this.sessionThreads, 
              this.totalThreadsCount,
              {
                currentPage: this.currentPage,
                totalPages: this.totalPages,
                hasMorePages: this.hasMorePages,
                pageSize: this.lockedPageSize || this.pageSize
              }
            );
            this.showDeleteSuccessMessage.emit();
            
            // Start a new agent chat (equivalent to Agent Header's onAgentNewChat)
            // This mirrors the effect of clicking "New Chat" in the agent header:
            // - clear agent chat history
            // - reset agent state to new chat and show landing
            this.agentOrchestrator.clearAgentChatHistory();
            this.agentOrchestrator.resetAgentState();
            
          } else {
            const translatedApologyMsg = this.translate.instant('APOLOGY_MESSAGE_DEFAULT');
            this.showPopupMessage.emit({
              functionname: "AgentDeleteChatSession",
              errorMessage: translatedApologyMsg,
              error: ""
            });
            this.logger.logError(
              response?.message || 'Failed to delete session',
              'SessionHistoryComponent.deleteSession'
            );
          }
        },
        error: (error) => {
          const translatedApologyMsg = this.translate.instant('APOLOGY_MESSAGE_DEFAULT');
            this.showPopupMessage.emit({
              functionname: "AgentDeleteChatSession",
              errorMessage: translatedApologyMsg,
              error: ""
            });
          this.logger.logError(
            `Error deleting session: ${error?.message || 'unknown'}`,
            'SessionHistoryComponent.deleteSession'
          );
        }
      })
    } catch (error) {
      this.logger.logError(`Error in deleteSession: ${error}`, 'SessionHistoryComponent.deleteSession');
    }
  }

  cancelDelete(thread: SessionThread): void {
    try {
      thread.isDeleting = false;
      this.selectedItemForAction = null;
    } catch (error) {
      this.logger.logError(`Error in cancelDelete: ${error}`, 'SessionHistoryComponent.cancelDelete');
    }
  }

  updateSession(thread: SessionThread): void {
    try {
      if (!thread.thread_name || thread.thread_name.trim() === '') {
        return;
      }
      if (thread.thread_name.length > 100) {
        return;
      }
      const request = {
        thread_id: thread.thread_id,
        user_id: this.userID,
        tenant_id: this.tenantID, 
        thread_name: thread.thread_name
        
      };
      // Close the rename popup immediately regardless of API result
      thread.is_edited = false;
      this.selectedItemForAction = null;
      this.renameAgentThreadService.RenameAgentThread(request, this.agentId).subscribe({
        next: (response) => {
          if (response.status?.toLowerCase() === 'success') {
            // Ensure subsequent cancel actions revert to the latest saved name (not the very first name)
            (thread as any).original_name = thread.thread_name;
            // Update cache after rename with current pagination state
            this.agentOrchestrator.cacheAgentThreads(
              this.agentId, 
              this.sessionThreads,
              undefined,
              {
                currentPage: this.currentPage,
                totalPages: this.totalPages,
                hasMorePages: this.hasMorePages,
                pageSize: this.lockedPageSize || this.pageSize
              }
            );
            this.showEditSuccessMessage.emit();
          } else {
            // Revert to previous name if rename failed
            if ((thread as any).original_name !== undefined) {
              thread.thread_name = (thread as any).original_name;
            }
            const translatedApologyMsg = this.translate.instant('APOLOGY_MESSAGE_DEFAULT');
            this.showPopupMessage.emit({
              functionname: "AgentUpdateChatSession",
              errorMessage: translatedApologyMsg,
              error: ""
            });
            this.logger.logError(
              response?.message || 'Failed to rename session',
              'SessionHistoryComponent.updateSession'
            );
          }
        },
        error: (error) => {
          // Revert to previous name on error
          if ((thread as any).original_name !== undefined) {
            thread.thread_name = (thread as any).original_name;
          }
          const translatedApologyMsg = this.translate.instant('APOLOGY_MESSAGE_DEFAULT');
            this.showPopupMessage.emit({
              functionname: "AgentUpdateChatSession",
              errorMessage: translatedApologyMsg,
              error: ""
            });
          this.logger.logError(
            `Error renaming session: ${error?.message || 'unknown'}`,
            'SessionHistoryComponent.updateSession'
          );
        }
      })
    } catch (error) {
      this.logger.logError(`Error in updateSession: ${error}`, 'SessionHistoryComponent.updateSession');
    }
  }

  cancelRename(thread: SessionThread): void {
    try {
      // Revert any in-place edits from the input
      if ((thread as any).original_name !== undefined) {
        thread.thread_name = (thread as any).original_name;
        delete (thread as any).original_name;
      }
      thread.is_edited = false;
      this.selectedItemForAction = null;
    } catch (error) {
      this.logger.logError(`Error in cancelRename: ${error}`, 'SessionHistoryComponent.cancelRename');
    }
  }

  shareSession(thread: SessionThread, event: Event): void {
    try {
      event.stopPropagation();

      // Close all dropdowns and reset other action states
      this.sessionThreads.forEach(t => {
        t.dropdownVisible = false;
        t.isDeleting = false;
        t.is_edited = false;
        t.is_shared = false;
      });

      const request: ShareAgentThreadRequest = {
        thread_id: thread.thread_id,
        tenant_id: this.tenantID,
        user_id: this.userID
      };

      this.shareAgentThreadService.ShareAgentThread(request, this.agentId).subscribe({
        next: (response) => {
          if (response.status?.toLowerCase() === 'success') {
            // Check if this is a placeholder response (no API endpoint configured)
            if ((response.data as any)?.placeholder) {
              // Set selectedItemForAction with placeholder flag
              this.selectedItemForAction = {
                ...thread,
                is_shared: true,
                share_link: null,
                placeholder: true
              };
            } else if (response.data?.share_link) {
              // Real share link from API
              // If the share_link is already a full URL, use it directly.
              // Otherwise (API token), build the full URL with agent_id.
              const rawLink = response.data.share_link;
              const shareLink = rawLink.startsWith('http')
                ? rawLink
                : `${window.location.origin}/agent-shared-chat/${rawLink}?tenant_id=${this.tenantID}&thread_id=${thread.thread_id}&agent_id=${this.agentId}`;
              // Set selectedItemForAction as a copy with share data to trigger the popup
              this.selectedItemForAction = {
                ...thread,
                is_shared: true,
                share_link: shareLink
              };
            } else {
              // Error case - success but no link and no placeholder
              const translatedApologyMsg = this.translate.instant('APOLOGY_MESSAGE_DEFAULT');
              this.showPopupMessage.emit({
                functionname: 'AgentShareSession',
                errorMessage: response?.message || translatedApologyMsg,
                error: ''
              });
              this.logger.logError(
                response?.message || 'Failed to generate share link',
                'SessionHistoryComponent.shareSession'
              );
            }
          } else {
            const translatedApologyMsg = this.translate.instant('APOLOGY_MESSAGE_DEFAULT');
            this.showPopupMessage.emit({
              functionname: 'AgentShareSession',
              errorMessage: response?.message || translatedApologyMsg,
              error: ''
            });
            this.logger.logError(
              response?.message || 'Failed to generate share link',
              'SessionHistoryComponent.shareSession'
            );
          }
        },
        error: (error) => {
          const translatedApologyMsg = this.translate.instant('APOLOGY_MESSAGE_DEFAULT');
          this.showPopupMessage.emit({
            functionname: 'AgentShareSession',
            errorMessage: translatedApologyMsg,
            error: ''
          });
          this.logger.logError(
            `Error sharing session: ${error?.message || 'unknown'}`,
            'SessionHistoryComponent.shareSession'
          );
        }
      });
    } catch (error) {
      this.logger.logError(`Error in shareSession: ${error}`, 'SessionHistoryComponent.shareSession');
    }
  }

  copyShareLink(thread: SessionThread): void {
    try {
      if (!this.selectedItemForAction?.share_link) return;

      const link = this.selectedItemForAction.share_link;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(link).then(() => {
          // Successfully copied - no longer showing toaster message
          // Keep popup open to show "Copied" state - don't close immediately
          // this.selectedItemForAction = null;
        }).catch(() => {
          this.fallbackCopyToClipboard(link);
        });
      } else {
        this.fallbackCopyToClipboard(link);
      }
    } catch (error) {
      this.logger.logError(`Error in copyShareLink: ${error}`, 'SessionHistoryComponent.copyShareLink');
      const translatedMsg = this.translate.instant('FAILED_TO_COPY_SHARED_LINK_ERR_MSG');
      this.showPopupMessage.emit({
        functionname: 'AgentShareSession',
        errorMessage: translatedMsg,
        error: ''
      });
    }
  }

  /**
   * Fallback clipboard copy using a hidden textarea and execCommand.
   * Used when navigator.clipboard is unavailable or fails.
   */
  private fallbackCopyToClipboard(text: string): void {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand('copy');
        // Successfully copied - no longer showing toaster message
        // Keep popup open to show "Copied" state - don't close immediately
        // this.selectedItemForAction = null;
      } catch (err) {
        this.logger.logError(`Failed to copy to clipboard: ${err}`, 'SessionHistoryComponent.fallbackCopyToClipboard');
        const translatedMsg = this.translate.instant('FAILED_TO_COPY_SHARED_LINK_ERR_MSG');
        this.showPopupMessage.emit({
          functionname: 'AgentShareSession',
          errorMessage: translatedMsg,
          error: ''
        });
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (error) {
      this.logger.logError(`Error in fallbackCopyToClipboard: ${error}`, 'SessionHistoryComponent.fallbackCopyToClipboard');
    }
  }

  cancelShare(thread: SessionThread): void {
    try {
      this.selectedItemForAction = null;
    } catch (error) {
      this.logger.logError(`Error in cancelShare: ${error}`, 'SessionHistoryComponent.cancelShare');
    }
  }

}





